import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { practitionerService } from '../../../services/practitionerService';

const PractitionerTable = ({ practitioners, onEdit, onDelete, onViewAccessLink }) => {
  const [sendingEmail, setSendingEmail] = React.useState(null);
  const [emailSuccess, setEmailSuccess] = React.useState(null);
  const [emailError, setEmailError] = React.useState(null);

  const handleSendCredentialsEmail = async (practitioner) => {
    setSendingEmail(practitioner?.id);
    setEmailError(null);
    setEmailSuccess(null);

    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session) {
        throw new Error('Non authentifié');
      }

      // Get user credentials for this practitioner
      const { data: userProfile } = await supabase?.from('user_profiles')?.select('email')?.eq('practitioner_id', practitioner?.id)?.single();

      if (!userProfile) {
        throw new Error('Aucun compte utilisateur trouvé pour ce praticien');
      }

      // Generate temporary password (in real scenario, this should be stored securely)
      const tempPassword = 'TempPass123!'; // This is just for demo - in production, use secure method

      const response = await fetch(
        `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-credentials-email`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            practitioner_id: practitioner?.id,
            email: userProfile?.email,
            password: tempPassword,
            practitioner_name: `${practitioner?.practitionerTitle} ${practitioner?.practitionerName}`,
            public_url: practitionerService?.getPublicUrl(practitioner?.slug),
            dashboard_url: `${window.location?.origin}/individual-practitioner-dashboard`
          }),
        }
      );

      const result = await response?.json();

      if (!response?.ok) {
        throw new Error(result.error || 'Erreur lors de l\'envoi de l\'email');
      }

      setEmailSuccess(practitioner?.id);
      setTimeout(() => setEmailSuccess(null), 3000);
    } catch (error) {
      console.error('Error sending credentials email:', error);
      setEmailError(practitioner?.id);
      setTimeout(() => setEmailError(null), 5000);
    } finally {
      setSendingEmail(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-success-bg text-success border border-success/30',
      pending: 'bg-warning-bg text-warning border border-warning/30',
      inactive: 'bg-card-elevated text-muted border border-white/8'
    };
    
    const labels = {
      active: 'Actif',
      pending: 'En attente',
      inactive: 'Inactif'
    };
    
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles?.[status] || styles?.inactive}`}>
        {labels?.[status] || status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString)?.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (!practitioners || practitioners?.length === 0) {
    return (
      <div className="bg-card rounded-2xl shadow-2xl border border-white/8 p-12 text-center">
        <Icon name="Users" size={48} className="text-muted mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-primary mb-2">Aucun praticien trouvé</h3>
        <p className="text-muted">Commencez par créer votre premier praticien</p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-2xl shadow-2xl border border-white/8 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-card-elevated border-b border-white/8">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-secondary uppercase tracking-wider">
                Cabinet / Praticien
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-secondary uppercase tracking-wider">
                Contact
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-secondary uppercase tracking-wider">
                URL Slug
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-secondary uppercase tracking-wider">
                Statut
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-secondary uppercase tracking-wider">
                Créé le
              </th>
              <th className="px-6 py-4 text-right text-xs font-semibold text-secondary uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/8">
            {practitioners?.map((practitioner) => (
              <tr key={practitioner?.id} className="hover:bg-card-hover transition-colors">
                <td className="px-6 py-4">
                  <div>
                    <p className="font-medium text-primary">{practitioner?.practiceName}</p>
                    <p className="text-sm text-muted">
                      {practitioner?.practitionerTitle} {practitioner?.practitionerName}
                    </p>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm">
                    <p className="text-secondary">{practitioner?.email}</p>
                    {practitioner?.phone && (
                      <p className="text-muted">{practitioner?.phone}</p>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <code className="px-2 py-1 bg-card-elevated rounded text-sm text-accent border border-white/8">
                    {practitioner?.slug}
                  </code>
                </td>
                <td className="px-6 py-4">
                  {getStatusBadge(practitioner?.status)}
                </td>
                <td className="px-6 py-4 text-sm text-muted">
                  {formatDate(practitioner?.createdAt)}
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      onClick={() => handleSendCredentialsEmail(practitioner)}
                      className="p-2 text-blue-400 hover:bg-blue-400/10 rounded-lg transition-colors"
                      title="Envoyer les identifiants par email"
                      disabled={sendingEmail === practitioner?.id}
                    >
                      {sendingEmail === practitioner?.id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400"></div>
                      ) : emailSuccess === practitioner?.id ? (
                        <Icon name="CheckCircle" size={18} />
                      ) : emailError === practitioner?.id ? (
                        <Icon name="XCircle" size={18} className="text-error" />
                      ) : (
                        <Icon name="Mail" size={18} />
                      )}
                    </Button>
                    <Button
                      onClick={() => onViewAccessLink?.(practitioner)}
                      className="p-2 text-accent hover:bg-accent/10 rounded-lg transition-colors"
                      title="Voir le lien d'accès"
                    >
                      <Icon name="Link" size={18} />
                    </Button>
                    <Button
                      onClick={() => onEdit?.(practitioner)}
                      className="p-2 text-secondary hover:bg-card-elevated rounded-lg transition-colors"
                      title="Modifier"
                    >
                      <Icon name="Edit" size={18} />
                    </Button>
                    <Button
                      onClick={() => onDelete?.(practitioner)}
                      className="p-2 text-error hover:bg-error-bg rounded-lg transition-colors"
                      title="Supprimer"
                    >
                      <Icon name="Trash2" size={18} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PractitionerTable;